import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import _get from 'lodash/get';
import _uniq from 'lodash/uniq';
import _pickBy from 'lodash/pickBy';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _omitBy from 'lodash/omitBy';
import { connect } from 'react-redux';
import { Select, Button, Input } from 'antd';

import { UserMe, EditSuggestions, UserReduxState, EditUser } from 'legacy/user/types';
import { LoadingState, ErrorState } from 'legacy/reducers/types';

import styles from './ProfileSettings.module.scss';
import profileStyles from '../Profile.module.scss';

import { editMe, uploadProfilePicture } from 'legacy/actions/userActions';
import { showModal } from 'legacy/actions/modalActions';

import DefaultUserPhoto from '../../../util/DefaultUserPhoto/DefaultUserPhoto';
import { usePrevious } from '../../../../util/hooks';

import { isEmail } from '../../../../util/validators';
import UploadProfilePictureButton from 'legacy/common/components/upload-photo/UploadProfilePictureButton';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FLAGS } from 'legacy/common/constants/launchdarkly';

import EmailSettings from 'legacy/user/components/settings/email/EmailSettings';
import PhoneNumberSettings from 'legacy/user/components/settings/phone/PhoneNumberSettings';

// Use shared EditUser type instead of duplicate PersonalFields
type PersonalFields = Pick<EditUser, 'email'> & {
  name?: string; // Keep name for display compatibility with existing user.name
};

interface FormErrors {
  name?: string;
  email?: string;
  [key: string]: any;
}

interface PersonalProps {
  user: UserMe;
  editMe: (data: any) => void;
  showModal: (type: string, config: any) => void;
  errors: ErrorState;
  loading: LoadingState;
  editSuggestions: EditSuggestions;
  hasChanged: boolean;
  setHasChanged: (value: boolean) => void;
}

const { Option } = Select;

const Personal = ({
  user = {} as UserMe,
  editMe,
  showModal,
  errors = {} as ErrorState,
  loading = {} as LoadingState,
  editSuggestions,
  hasChanged,
  setHasChanged
}: PersonalProps) => {
  const { t } = useTranslation(['users']);
  const flags = useFlags();

  const { profile = {} as UserMe['profile'], name, email } = user;
  const sanitizedSocialMedia = _omitBy(
    profile?.socialMedia || {},
    (socialMedia) => !socialMedia.username
  ); // Hack to remove empty social media entries for the hasChanged comparison
  const [photoURL, setPhotoURL] = useState<string | undefined>();

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const [fields, setFields] = useState<PersonalFields>({ name, email });

  // eslint-disable-next-line
  const handleFieldChange = (key, value) => {
    setFields((fields) => ({
      ...fields,
      [key]: value
    }));
  };

  useEffect(() => {
    setFormErrors((prev) => ({
      ...prev,
      name: !fields.name && t('users:settings.personal.form.nameRequired'),
      email: isEmail(fields?.email || '')
    }));
  }, [fields, name, fields.email, t]);

  const handleChange = (evt) => {
    const { name, value } = evt.target;
    handleFieldChange(name, value);
  };

  const prevLoading = usePrevious(loading.EDIT_ME);
  useEffect(() => {
    if (!loading.EDIT_ME && prevLoading) {
      if (_isEmpty(errors.EDIT_ME)) {
        showModal('ALERT', {
          message: t('users:settings.personal.success'),
          type: 'success'
        });
        setHasChanged(false); // Hack to not show the unsaved changes alert after saving
      } else {
        showModal('ALERT', { message: errors.EDIT_ME.message });
      }
    }
  }, [loading.EDIT_ME, errors.EDIT_ME]);

  const {
    interests = [],
    socialMedia = {},
    profession,
    education = {},
    gallery = []
  } = profile || {};

  const [localGallery, setLocalGallery] = useState(gallery);
  const emptyPics = [];
  for (let i = 0; i < 3 - localGallery.length; i++) {
    emptyPics.push('');
  }

  const clearGalleryItem = (index) => {
    setLocalGallery((current) => [
      ...current.slice(0, index),
      ...current.slice(index + 1)
    ]);
  };

  const [localInterests, setLocalInterests] = useState(interests);

  const [localSocialMedia, setLocalSocialMedia] = useState(
    _pickBy(socialMedia, ({ username, url }) => username || url)
  );
  const socialMediaTypes = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    snapchat: 'Snapchat',
    twitter: 'Twitter'
  };
  const availableSocialMedias = _pickBy(
    socialMediaTypes,
    (value, key) =>
      !_get(localSocialMedia, `${key}.username`) &&
      !_get(localSocialMedia, `${key}.url`)
  );

  const addSocialMedia = (media) => {
    setLocalSocialMedia((current) => ({
      ...current,
      [media]: {}
    }));
    setFormErrors((prev) => ({ ...prev, [media]: t('users:settings.personal.socialMedia.usernameRequired') }));
  };

  const disconnectSocialMedia = (media) => {
    setLocalSocialMedia(({ [media]: _omit, ...rest }) => rest);
    setFormErrors(({ [media]: _omit, ...rest }) => rest);
  };

  const [visited, setVisited] = useState({});

  const changeMedia = (evt, media) => {
    const { value } = evt.target;
    setLocalSocialMedia((current) => ({
      ...current,
      [media]: {
        ...current[media],
        username: value
      }
    }));
    setFormErrors((prev) => ({
      ...prev,
      [media]: !value && t('users:settings.personal.socialMedia.usernameRequired')
    }));
    setVisited((prev) => ({ ...prev, [media]: true }));
  };

  const schools = _uniq(_get(editSuggestions, 'profile.education.school', []));
  const programs = _uniq(
    _get(editSuggestions, 'profile.education.program', [])
  );
  const professions = _uniq(_get(editSuggestions, 'profile.profession', []));

  const [localProfession, setLocalProfession] = useState(profession);
  const [localEducation, setLocalEducation] = useState(education);

  const saveFields = () => {
    setHasAttemptedSave(true);
    if (_isEmpty(_pickBy(formErrors, (val) => val))) {
      editMe({
        ...fields,
        profile: {
          interests: localInterests,
          socialMedia: {
            ...localSocialMedia,
            ...Object.keys(availableSocialMedias).reduce(
              (acc, key) => ({
                ...acc,
                [key]: { url: null, username: null }
              }),
              {}
            )
          },
          education: {
            school: localEducation.school || null,
            program: localEducation.program || null
          },
          profession: localProfession || null,
          gallery: localGallery
        }
      });
    }
  };

  useEffect(() => {
    if (
      fields.name !== name ||
      fields.email !== email ||
      !_isEqual(profile?.education, localEducation) ||
      !_isEqual(sanitizedSocialMedia, localSocialMedia) ||
      !_isEqual(profile?.profession, localProfession)
    ) {
      setHasChanged(true);
    } else if (hasChanged) {
      setHasChanged(false);
    }
  }, [
    fields,
    localEducation,
    localInterests,
    localProfession,
    localSocialMedia
  ]);

  return (
    <>
      <h1>{t('users:settings.personal.title')}</h1>

      <div className={styles.editProfileRow}>
        <UploadProfilePictureButton
          onUpload={(url) => {
            setPhotoURL(url);
          }}
          ButtonComponent={({ onClick }) => (
            <div className={profileStyles.profilePicWrapper} onClick={onClick}>
              <div className={profileStyles.uploadOverlay}>
                <i className="fas fa-upload" />
              </div>
              {photoURL || (user.photo && user.photo.url) ? (
                <img src={photoURL || user.photo.url} alt="" />
              ) : (
                <DefaultUserPhoto style={{ fontSize: '50px' }} />
              )}
            </div>
          )}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <Input
            className={cn(styles.input, {
              [styles.hasError]: formErrors.name
            })}
            addonBefore={t('users:settings.personal.form.name')}
            name="name"
            value={fields.name || ''}
            onChange={handleChange}
          />
          {formErrors.name && <p className={styles.error}>{formErrors.name}</p>}

          {flags[FLAGS.ENABLE_EMAIL_EDIT] ? (
            <EmailSettings />
          ) : (
            <>
              <Input
                className={cn(styles.input, {
                  [styles.hasError]: formErrors.email
                })}
                addonBefore={t('users:settings.personal.form.email')}
                name="email"
                value={fields.email || ''}
                onChange={handleChange}
              />
              {formErrors.email && (
                <p className={styles.error}>{formErrors.email}</p>
              )}
            </>
          )}

          <PhoneNumberSettings />
        </div>
      </div>

      <h2>{t('users:settings.personal.school.title')}</h2>
      <div className={styles.schoolRow} style={{ width: '100%' }}>
        <Select
          allowClear
          showSearch
          onChange={(val) =>
            setLocalEducation((education) => ({
              ...education,
              program: val
            }))
          }
          value={localEducation.program}
          placeholder={t('users:settings.personal.school.program')}
        >
          {programs.map((program) => (
            <Option key={program as string} value={program as string}>
              {program as string}
            </Option>
          ))}
        </Select>
        <span>{t('users:settings.personal.school.at')}</span>
        <Select
          allowClear
          showSearch
          onChange={(val) =>
            setLocalEducation((education) => ({
              ...education,
              school: val
            }))
          }
          value={localEducation.school}
          placeholder={t('users:settings.personal.school.school')}
        >
          {schools.map((school) => (
            <Option key={school as string} value={school as string}>
              {school as string}
            </Option>
          ))}
        </Select>
      </div>

      <h2>{t('users:settings.personal.profession.title')}</h2>
      <Select
        allowClear
        showSearch
        placeholder={t('users:settings.personal.profession.placeholder')}
        style={{ width: '100%' }}
        value={localProfession}
        onChange={(val) => setLocalProfession(val)}
      >
        {professions.map((profession) => (
          <Option key={profession as string} value={profession as string}>
            {profession as string}
          </Option>
        ))}
      </Select>

      <h2>{t('users:settings.personal.socialMedia.title')}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} className={styles.socialMedia}>
        {Object.entries(localSocialMedia).map(([media, mediaValue]) => (
          <React.Fragment key={media}>
            <Input
              className={cn(styles.input, {
                [styles.hasError]:
                  formErrors[media] && (visited[media] || hasAttemptedSave)
              })}
              style={{ width: '100%' }}
              addonBefore={t('users:settings.personal.socialMedia.connectedWith', { platform: socialMediaTypes[media] })}
              addonAfter={
                <span
                  className={styles.disconnect}
                  onClick={() => disconnectSocialMedia(media)}
                >
                  {t('users:settings.personal.socialMedia.disconnect')}
                </span>
              }
              value={(mediaValue as any)?.username || ''}
              placeholder={t('users:settings.personal.socialMedia.usernamePlaceholder')}
              onChange={(evt) => changeMedia(evt, media)}
            />
            {formErrors[media] && (visited[media] || hasAttemptedSave) && (
              <p className={styles.error}>{formErrors[media]}</p>
            )}
          </React.Fragment>
        ))}
        {!_isEmpty(availableSocialMedias) && (
          <Select
            value={t('users:settings.personal.socialMedia.addSocialMedia')}
            style={{ width: '50%', minWidth: '300px' }}
            onChange={addSocialMedia}
          >
            {Object.entries(availableSocialMedias).map(([key, value]) => (
              <Option key={key} value={key}>
                {value as string}
              </Option>
            ))}
          </Select>
        )}
      </div>
      <h2>{t('users:settings.personal.interests.title')}</h2>
      <Select
        mode="multiple"
        style={{ width: '100%' }}
        placeholder={t('users:settings.personal.interests.placeholder')}
        value={localInterests}
        onChange={(val) => setLocalInterests(val)}
      >
        {_get(editSuggestions, 'profile.interests', [])
          .sort()
          .map((option) => (
            <Option key={option} value={option}>
              {option}
            </Option>
          ))}
      </Select>

      <h2>{t('users:settings.personal.gallery.title')}</h2>
      <div className={styles.gallery}>
        {localGallery.map((pic, i) => {
          const picUrl = typeof pic === 'string' ? pic : pic?.url;
          return (
            <div
              key={typeof pic === 'string' ? pic : pic?._id || i}
              className={styles.pic}
              style={{ backgroundImage: `url(${picUrl})` }}
              data-cy="gallery-image"
            >
            <div
              className={styles.removePic}
              onClick={() => clearGalleryItem(i)}
              data-cy="remove-gallery-image-button"
            >
              <i className="fa fa-times" />
            </div>
            </div>
          );
        })}
        {emptyPics.map((pic) => (
          <div
            key={pic}
            className={styles.pic}
            onClick={() =>
              showModal('ALL_PHOTOS', {
                setLocalGallery,
                localGallery
              })
            }
            data-cy="empty-gallery-image"
          >
            <i className="fa fa-plus" />
          </div>
        ))}
      </div>
      <Button
        className={styles.submit}
        onClick={saveFields}
        loading={loading.EDIT_ME}
      >
        {t('users:settings.personal.save')}
      </Button>
    </>
  );
};

const mapStateToProps = (state: { user: UserReduxState }) => ({
  loading: state.user.loading,
  errors: state.user.errors,
  editSuggestions: state.user.editSuggestions
});

const actions = {
  editMe,
  showModal,
  uploadProfilePicture
};

export default connect(mapStateToProps, actions)(Personal);
